/**
 * useTable Hook
 * Combines pagination, filtering, sorting, selection
 * Reduces table state management boilerplate
 */

import { useState, useCallback, useMemo } from 'react';

/**
 * Table state management hook
 *
 * @param {object} config - Configuration
 * @param {number} config.defaultPageSize - Initial page size (default: 20)
 * @param {object} config.defaultFilters - Initial filters
 * @param {object} config.defaultSort - Initial sort {field, order: 'ascend'|'descend'}
 * @param {boolean} config.enableSelection - Enable row selection
 * @returns {object} - Complete table state and handlers
 *
 * @example
 * const table = useTable({ defaultPageSize: 50 });
 * <Table
 *   {...table.tableProps}
 *   dataSource={data}
 *   columns={columns}
 * />
 */
function useTable({
  defaultPageSize = 20,
  defaultFilters = {},
  defaultSort = null,
  enableSelection = false,
} = {}) {
  // Pagination state
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: defaultPageSize,
    total: 0,
  });

  // Filters state
  const [filters, setFilters] = useState(defaultFilters);

  // Sort state
  const [sorter, setSorter] = useState(defaultSort);

  // Selection state
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);

  // Search state (separate from filters for quick access)
  const [searchText, setSearchText] = useState('');

  // Handle table change (pagination, filters, sorter)
  const handleTableChange = useCallback((newPagination, newFilters, newSorter) => {
    setPagination({
      ...pagination,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    });
    setFilters(newFilters);
    setSorter(newSorter);
  }, [pagination]);

  // Update total count (call this when data is fetched)
  const setTotal = useCallback((total) => {
    setPagination((prev) => ({ ...prev, total }));
  }, []);

  // Reset pagination to first page
  const resetPagination = useCallback(() => {
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, []);

  // Update filters
  const updateFilters = useCallback((newFilters) => {
    setFilters(newFilters);
    resetPagination(); // Reset to first page when filtering
  }, [resetPagination]);

  // Clear filters
  const clearFilters = useCallback(() => {
    setFilters({});
    resetPagination();
  }, [resetPagination]);

  // Update search text
  const updateSearch = useCallback((text) => {
    setSearchText(text);
    resetPagination();
  }, [resetPagination]);

  // Selection handlers
  const rowSelection = enableSelection
    ? {
        selectedRowKeys,
        onChange: (keys, rows) => {
          setSelectedRowKeys(keys);
          setSelectedRows(rows);
        },
      }
    : undefined;

  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedRowKeys([]);
    setSelectedRows([]);
  }, []);

  // Build Ant Design Table props
  const tableProps = useMemo(
    () => ({
      pagination: {
        ...pagination,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
        pageSizeOptions: ['10', '20', '50', '100'],
      },
      onChange: handleTableChange,
      rowSelection,
    }),
    [pagination, handleTableChange, rowSelection]
  );

  return {
    // Pagination
    pagination,
    setPagination,
    setTotal,
    resetPagination,

    // Filters
    filters,
    setFilters: updateFilters,
    clearFilters,

    // Sort
    sorter,
    setSorter,

    // Search
    searchText,
    setSearchText: updateSearch,

    // Selection
    selectedRowKeys,
    selectedRows,
    setSelectedRowKeys,
    setSelectedRows,
    clearSelection,

    // Table props (spread directly onto Table component)
    tableProps,
  };
}

export default useTable;
